/**
 * VAT crowd (P1-T11, reduced rig vs prototype v3: 7 bones of boxes, no
 * fingers). Instead of Babylon's stock baked-vertex-animation (which exploded
 * with our hand-authored skinning), the gait is analytic FK inside a material
 * plugin: each box rotates about its bone pivot by sin(2π·(t·cycles + phase))
 * — deterministic, zero textures, zero CPU skinning. Per-person colours come
 * from the KNPAL palette plugin (kreg region + kA..kD instance colours).
 */
import {
  Color3,
  Material,
  MaterialPluginBase,
  Matrix,
  Mesh,
  PBRMaterial,
  Quaternion,
  Vector3,
  VertexBuffer,
  VertexData,
  type AbstractEngine,
  type Scene,
} from 'babylonjs';
import { prng } from '@kota-nova/sim';

export interface CrowdLook {
  fem: boolean;
  skin: [number, number, number];
  top: [number, number, number];
  bottom: [number, number, number];
  hair: [number, number, number];
  legs: number; // 1 = bare legs
  scale: number;
}

const SKIN: Array<[number, number, number]> = [
  [0.96, 0.8, 0.68],
  [0.86, 0.66, 0.52],
  [0.7, 0.5, 0.38],
  [0.5, 0.34, 0.25],
  [0.32, 0.22, 0.17],
];
const TOPS: Array<[number, number, number]> = [
  [0.75, 0.25, 0.22],
  [0.2, 0.35, 0.6],
  [0.9, 0.88, 0.82],
  [0.15, 0.45, 0.4],
  [0.35, 0.35, 0.4],
  [0.85, 0.6, 0.25],
  [0.25, 0.25, 0.3],
  [0.6, 0.55, 0.7],
];
const BOTTOMS: Array<[number, number, number]> = [
  [0.15, 0.2, 0.35],
  [0.1, 0.1, 0.12],
  [0.45, 0.42, 0.38],
  [0.3, 0.28, 0.25],
  [0.55, 0.55, 0.58],
];
const HAIRS: Array<[number, number, number]> = [
  [0.1, 0.08, 0.07],
  [0.3, 0.2, 0.12],
  [0.6, 0.5, 0.35],
  [0.15, 0.15, 0.18],
  [0.45, 0.3, 0.2],
];

function pick<T>(a: T[], r: number): T {
  return a[Math.min(a.length - 1, Math.floor(r * a.length))] as T;
}

export function crowdLook(seed: number): CrowdLook {
  const r = prng(seed * 9301 + 49297);
  const fem = r() < 0.5;
  return {
    fem,
    skin: pick(SKIN, r()),
    top: pick(TOPS, r()),
    bottom: pick(BOTTOMS, r()),
    hair: pick(HAIRS, r()),
    legs: fem ? (r() < 0.35 ? 1 : 0) : r() < 0.12 ? 1 : 0,
    scale: (fem ? 0.93 : 0.99) + r() * 0.09,
  };
}

/** Shared gait clock the plugin samples (advanced by CrowdField every frame). */
export const CrowdClock = { time: 0 };

// One inline gait block, duplicated into the position and normal hooks (the
// *_UPDATE_* tokens inject inside main(), so no helper functions are allowed
// there; the facade plugin proved this constraint on 9.29).
// kbone: 3 armL, 4 armR, 5 legL, 6 legR, 1 torso, 2 head, 0 hips.
// kph:   x phase 0..1, y gait cycles/s, z amplitude (0 = standing).
const GAIT_POS = `
#ifdef KN_GAIT
{
  float b = floor(kbone + 0.5);
  float ph = 6.2831853 * (knGaitTime * kph.y + kph.x);
  float s = sin(ph);
  float a = 0.0;
  if (b > 4.5 && b < 5.5) a = 0.62 * s;
  else if (b > 5.5 && b < 6.5) a = -0.62 * s;
  else if (b > 2.5 && b < 3.5) a = -0.5 * s;
  else if (b > 3.5 && b < 4.5) a = 0.5 * s;
  else if (b > 0.5 && b < 1.5) a = 0.05;
  else if (b > 1.5 && b < 2.5) a = -0.05;
  a *= kph.z;
  if (abs(a) > 0.0005) {
    vec3 pv = vec3(0.0);
    if (b > 4.5 && b < 5.5) pv = vec3(-0.09, 0.78, 0.0);
    else if (b > 5.5 && b < 6.5) pv = vec3(0.09, 0.78, 0.0);
    else if (b > 2.5 && b < 3.5) pv = vec3(-0.25, 1.45, 0.0);
    else if (b > 3.5 && b < 4.5) pv = vec3(0.25, 1.45, 0.0);
    else if (b > 0.5 && b < 1.5) pv = vec3(0.0, 0.78, 0.0);
    else if (b > 1.5 && b < 2.5) pv = vec3(0.0, 1.5, 0.0);
    float ca = cos(a), sa = sin(a);
    float ry = positionUpdated.y - pv.y, rz = positionUpdated.z - pv.z;
    positionUpdated.y = pv.y + ca * ry - sa * rz;
    positionUpdated.z = pv.z + sa * ry + ca * rz;
  }
  positionUpdated.y += 0.02 * kph.z * abs(sin(ph));
}
#endif
`;

const GAIT_NOR = `
#ifdef KN_GAIT
{
  float b = floor(kbone + 0.5);
  float ph = 6.2831853 * (knGaitTime * kph.y + kph.x);
  float s = sin(ph);
  float a = 0.0;
  if (b > 4.5 && b < 5.5) a = 0.62 * s;
  else if (b > 5.5 && b < 6.5) a = -0.62 * s;
  else if (b > 2.5 && b < 3.5) a = -0.5 * s;
  else if (b > 3.5 && b < 4.5) a = 0.5 * s;
  else if (b > 0.5 && b < 1.5) a = 0.05;
  else if (b > 1.5 && b < 2.5) a = -0.05;
  a *= kph.z;
  if (abs(a) > 0.0005) {
    float ca = cos(a), sa = sin(a);
    float ny = normalUpdated.y, nz = normalUpdated.z;
    normalUpdated.y = ca * ny - sa * nz;
    normalUpdated.z = sa * ny + ca * nz;
  }
}
#endif
`;

/**
 * KNGAIT: box pivots rotate by sin(2π·(t·cycles + phase)) for legs/arms;
 * amp 0 = standing, 1 = walking.
 */
export class KNGaitPlugin extends MaterialPluginBase {
  constructor(material: Material) {
    super(material, 'KNGait', 209, { KN_GAIT: false }, true, true);
  }
  override prepareDefines(defines: Record<string, unknown>): void {
    defines['KN_GAIT'] = true;
  }
  override getClassName(): string {
    return 'KNGaitPlugin';
  }
  override getAttributes(attributes: string[]): void {
    for (const a of ['kbone', 'kph']) if (!attributes.includes(a)) attributes.push(a);
  }
  override getUniforms(): {
    ubo: Array<{ name: string; size: number; type: string }>;
    vertex: string;
  } {
    return {
      ubo: [{ name: 'knGaitTime', size: 1, type: 'float' }],
      vertex: '#ifdef KN_GAIT\nuniform float knGaitTime;\n#endif\n',
    };
  }
  override bindForSubMesh(ubo: { updateFloat(name: string, v: number): void }): void {
    ubo.updateFloat('knGaitTime', CrowdClock.time);
  }
  override getCustomCode(type: string): Record<string, string> | null {
    if (type !== 'vertex') return null;
    return {
      // custom attributes MUST be declared at global scope (prototype recipe;
      // getUniforms().vertex does not emit attribute lines on 9.29)
      CUSTOM_VERTEX_DEFINITIONS: '#ifdef KN_GAIT\nattribute float kbone;\nattribute vec4 kph;\n#endif\n',
      CUSTOM_VERTEX_UPDATE_POSITION: GAIT_POS,
      CUSTOM_VERTEX_UPDATE_NORMAL: GAIT_NOR,
    };
  }
}

/**
 * KNPAL palette plugin (prototype v3 recipe): paints vertices by their kreg
 * region id using per-instance colours kA skin / kB top / kC bottom / kD hair.
 */
export class KNPALPlugin extends MaterialPluginBase {
  constructor(material: Material) {
    super(material, 'KNPAL', 210, { KNPAL: false });
    this._enable(true);
  }
  override prepareDefines(defines: Record<string, unknown>): void {
    defines['KNPAL'] = true;
  }
  override getClassName(): string {
    return 'KNPALPlugin';
  }
  override getAttributes(attributes: string[]): void {
    for (const a of ['kreg', 'kA', 'kB', 'kC', 'kD']) if (!attributes.includes(a)) attributes.push(a);
  }
  override getCustomCode(type: string): Record<string, string> | null {
    if (type === 'vertex') {
      return {
        CUSTOM_VERTEX_DEFINITIONS:
          '#ifdef KNPAL\nattribute float kreg;\nattribute vec4 kA;\nattribute vec4 kB;\nattribute vec4 kC;\nattribute vec4 kD;\nvarying vec3 vKpal;\n#endif\n',
        CUSTOM_VERTEX_MAIN_END: `#ifdef KNPAL
{ float r = floor(kreg + 0.5); vec3 c = kA.rgb;
  if (r > 0.5 && r < 1.5) c = kB.rgb;
  else if (r > 1.5 && r < 2.5) c = kC.rgb;
  else if (r > 2.5 && r < 3.5) c = vec3(0.06, 0.06, 0.07);
  else if (r > 3.5 && r < 4.5) c = kD.rgb;
  vKpal = c; }
#endif
`,
      };
    }
    if (type === 'fragment') {
      return {
        CUSTOM_FRAGMENT_DEFINITIONS: '#ifdef KNPAL\nvarying vec3 vKpal;\n#endif\n',
        CUSTOM_FRAGMENT_UPDATE_ALBEDO: '#ifdef KNPAL\nsurfaceAlbedo *= vKpal;\n#endif\n',
      };
    }
    return null;
  }
}

interface BoxSpec {
  bone: number; // 0 hips 1 torso 2 head 3 armL 4 armR 5 legL 6 legR
  c: [number, number, number]; // centre in rest body space (y up, feet 0)
  s: [number, number, number];
  reg: number; // 0 skin 1 top 2 bottom 3 shoes 4 hair
}

function bodyBoxes(fem: boolean): BoxSpec[] {
  const w = fem ? 0.155 : 0.175;
  return [
    { bone: 0, c: [0, 0.87, 0], s: [w * 2, 0.2, 0.16], reg: 2 }, // pelvis (hips)
    { bone: 1, c: [0, 1.13, 0], s: [w * 2 + 0.03, 0.42, 0.17], reg: 1 }, // chest
    { bone: 2, c: [0, 1.62, 0], s: [0.17, 0.21, 0.18], reg: 0 }, // head
    { bone: 2, c: [0, 1.74, 0], s: [0.185, 0.1, 0.195], reg: 4 }, // hair
    { bone: 3, c: [-(w + 0.08), 1.19, 0], s: [0.09, 0.5, 0.09], reg: 1 }, // armL
    { bone: 4, c: [w + 0.08, 1.19, 0], s: [0.09, 0.5, 0.09], reg: 1 }, // armR
    { bone: 5, c: [-0.09, 0.43, 0], s: [0.12, 0.7, 0.12], reg: 2 }, // legL
    { bone: 6, c: [0.09, 0.43, 0], s: [0.12, 0.7, 0.12], reg: 2 }, // legR
    { bone: 5, c: [-0.09, 0.035, 0.035], s: [0.13, 0.07, 0.24], reg: 3 }, // shoeL
    { bone: 6, c: [0.09, 0.035, 0.035], s: [0.13, 0.07, 0.24], reg: 3 }, // shoeR
  ];
}

function bodyBoxesFar(fem: boolean): BoxSpec[] {
  const w = fem ? 0.155 : 0.175;
  return [
    { bone: 1, c: [0, 1.15, 0], s: [w * 2 + 0.04, 0.76, 0.22], reg: 1 },
    { bone: 2, c: [0, 1.67, 0], s: [0.19, 0.17, 0.19], reg: 4 },
    { bone: 5, c: [-0.07, 0.4, 0], s: [0.16, 0.78, 0.15], reg: 2 },
    { bone: 6, c: [0.07, 0.4, 0], s: [0.16, 0.78, 0.15], reg: 2 },
  ];
}

/** Ground travel per full cycle at scale 1 (cycles/s = speed / this). */
export const CROWD_STRIDE_M = 1.1;

interface Variant {
  mesh: Mesh;
  cap: number;
  n: number;
  mat: Float32Array;
  kph: Float32Array;
  kA: Float32Array;
  kB: Float32Array;
  kC: Float32Array;
  kD: Float32Array;
}

export interface CrowdFieldOptions {
  tier: 'low' | 'mid' | 'high';
  capMid: number;
  capFar: number;
  radius: number;
}

function pushQuad(pos: number[], nor: number[], idx: number[], p: number[][], n: number[]): void {
  const base = pos.length / 3;
  for (const pt of p) {
    pos.push(pt[0] as number, pt[1] as number, pt[2] as number);
    nor.push(n[0] as number, n[1] as number, n[2] as number);
  }
  idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

function addBox(bx: BoxSpec, pos: number[], nor: number[], idx: number[]): void {
  const [cx, cy, cz] = bx.c;
  const hx = bx.s[0] / 2;
  const hy = bx.s[1] / 2;
  const hz = bx.s[2] / 2;
  pushQuad(
    pos,
    nor,
    idx,
    [
      [cx - hx, cy - hy, cz + hz],
      [cx + hx, cy - hy, cz + hz],
      [cx + hx, cy + hy, cz + hz],
      [cx - hx, cy + hy, cz + hz],
    ],
    [0, 0, 1],
  );
  pushQuad(
    pos,
    nor,
    idx,
    [
      [cx + hx, cy - hy, cz - hz],
      [cx - hx, cy - hy, cz - hz],
      [cx - hx, cy + hy, cz - hz],
      [cx + hx, cy + hy, cz - hz],
    ],
    [0, 0, -1],
  );
  pushQuad(
    pos,
    nor,
    idx,
    [
      [cx + hx, cy - hy, cz + hz],
      [cx + hx, cy - hy, cz - hz],
      [cx + hx, cy + hy, cz - hz],
      [cx + hx, cy + hy, cz + hz],
    ],
    [1, 0, 0],
  );
  pushQuad(
    pos,
    nor,
    idx,
    [
      [cx - hx, cy - hy, cz - hz],
      [cx - hx, cy - hy, cz + hz],
      [cx - hx, cy + hy, cz + hz],
      [cx - hx, cy + hy, cz - hz],
    ],
    [-1, 0, 0],
  );
  pushQuad(
    pos,
    nor,
    idx,
    [
      [cx - hx, cy + hy, cz + hz],
      [cx + hx, cy + hy, cz + hz],
      [cx + hx, cy + hy, cz - hz],
      [cx - hx, cy + hy, cz - hz],
    ],
    [0, 1, 0],
  );
  pushQuad(
    pos,
    nor,
    idx,
    [
      [cx - hx, cy - hy, cz - hz],
      [cx + hx, cy - hy, cz - hz],
      [cx + hx, cy - hy, cz + hz],
      [cx - hx, cy - hy, cz + hz],
    ],
    [0, -1, 0],
  );
}

export class CrowdField {
  readonly meshes: Mesh[] = [];
  private readonly variants: Record<string, Variant> = {};

  constructor(
    private readonly engine: AbstractEngine,
    scene: Scene,
    private readonly opts: CrowdFieldOptions,
  ) {
    // declare vKpal once (vertex side) via Effect fallback: KNPAL's vertex
    // defines compile with the varying emitted from the plugin below
    const makeMesh = (fem: boolean, far: boolean): Mesh => {
      const boxes = far ? bodyBoxesFar(fem) : bodyBoxes(fem);
      const pos: number[] = [];
      const nor: number[] = [];
      const idx: number[] = [];
      const reg: number[] = [];
      const bone: number[] = [];
      for (const bx of boxes) {
        const v0 = pos.length / 3;
        addBox(bx, pos, nor, idx);
        for (let v = v0; v < pos.length / 3; v++) {
          reg[v] = bx.reg;
          bone[v] = bx.bone;
        }
      }
      const mesh = new Mesh(`crowd_${far ? 'far' : 'mid'}${fem ? 'F' : 'M'}`, scene);
      const vd = new VertexData();
      vd.positions = pos;
      vd.normals = nor;
      vd.indices = idx;
      vd.applyToMesh(mesh, false);
      mesh.setVerticesBuffer(new VertexBuffer(this.engine, reg, 'kreg', false, false, 1));
      mesh.setVerticesBuffer(new VertexBuffer(this.engine, bone, 'kbone', false, false, 1));
      mesh.alwaysSelectAsActiveMesh = true;
      mesh.isPickable = false;
      mesh.receiveShadows = true;
      return mesh;
    };

    const mat = new PBRMaterial('crowdBody', scene);
    mat.albedoColor = new Color3(1, 1, 1);
    mat.metallic = 0;
    mat.roughness = 0.72;
    void new KNGaitPlugin(mat);
    void new KNPALPlugin(mat);

    for (const far of [false, true]) {
      for (const fem of [false, true]) {
        const kind = far ? 'far' : 'mid';
        const mesh = makeMesh(fem, far);
        mesh.material = mat;
        const cap = far ? this.opts.capFar : this.opts.capMid;
        const v: Variant = {
          mesh,
          cap,
          n: 0,
          mat: new Float32Array(cap * 16),
          kph: new Float32Array(cap * 4),
          kA: new Float32Array(cap * 4),
          kB: new Float32Array(cap * 4),
          kC: new Float32Array(cap * 4),
          kD: new Float32Array(cap * 4),
        };
        // park slot 0 far below the map; thin-instance count is never 0 (D-16)
        v.mat.set([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, -500, 0, 1], 0);
        mesh.thinInstanceSetBuffer('matrix', v.mat, 16, false);
        mesh.thinInstanceSetBuffer('kph', v.kph, 4, false);
        mesh.thinInstanceSetBuffer('kA', v.kA, 4, false);
        mesh.thinInstanceSetBuffer('kB', v.kB, 4, false);
        mesh.thinInstanceSetBuffer('kC', v.kC, 4, false);
        mesh.thinInstanceSetBuffer('kD', v.kD, 4, false);
        mesh.thinInstanceCount = 1;
        this.variants[kind + (fem ? 'F' : 'M')] = v;
        this.meshes.push(mesh);
      }
    }
  }

  /** Begin a submission frame; advances the gait clock. */
  begin(dtMs: number): void {
    CrowdClock.time += Math.min(0.1, dtMs / 1000);
    for (const v of Object.values(this.variants)) v.n = 0;
  }

  push(
    lod: 'mid' | 'far',
    look: CrowdLook,
    x: number,
    y: number,
    z: number,
    yaw: number,
    clip: 'walk' | 'idle',
    phase: number,
    speed: number,
  ): boolean {
    const v = this.variants[(lod === 'mid' ? 'mid' : 'far') + (look.fem ? 'F' : 'M')];
    if (!v || v.n >= v.cap) return false;
    const i = v.n++;
    const q = Quaternion.RotationYawPitchRoll(yaw, 0, 0);
    const m = Matrix.Compose(new Vector3(look.scale, look.scale, look.scale), q, new Vector3(x, y, z));
    m.copyToArray(v.mat, i * 16);
    // cycles/s matched to ground speed (no foot slide); amp 0 = standing
    const cycles = clip === 'walk' ? speed / CROWD_STRIDE_M : 0;
    v.kph[i * 4] = phase;
    v.kph[i * 4 + 1] = cycles;
    v.kph[i * 4 + 2] = clip === 'walk' ? 1 : 0;
    v.kph[i * 4 + 3] = 0;
    v.kA[i * 4] = look.skin[0];
    v.kA[i * 4 + 1] = look.skin[1];
    v.kA[i * 4 + 2] = look.skin[2];
    v.kA[i * 4 + 3] = 0;
    v.kB[i * 4] = look.top[0];
    v.kB[i * 4 + 1] = look.top[1];
    v.kB[i * 4 + 2] = look.top[2];
    v.kB[i * 4 + 3] = 0;
    v.kC[i * 4] = look.bottom[0];
    v.kC[i * 4 + 1] = look.bottom[1];
    v.kC[i * 4 + 2] = look.bottom[2];
    v.kC[i * 4 + 3] = look.legs;
    v.kD[i * 4] = look.hair[0];
    v.kD[i * 4 + 1] = look.hair[1];
    v.kD[i * 4 + 2] = look.hair[2];
    v.kD[i * 4 + 3] = 0;
    return true;
  }

  /** Commit the frame; returns total visible people. */
  end(): number {
    let tot = 0;
    for (const v of Object.values(this.variants)) {
      if (v.n > 0) {
        v.mesh.thinInstanceCount = v.n;
        v.mesh.setEnabled(true);
        v.mesh.thinInstanceBufferUpdated('matrix');
        v.mesh.thinInstanceBufferUpdated('kph');
        for (const a of ['kA', 'kB', 'kC', 'kD'] as const) v.mesh.thinInstanceBufferUpdated(a);
      } else {
        v.mesh.thinInstanceCount = 1; // parked matrix, never 0 (D-16)
        v.mesh.setEnabled(false);
      }
      tot += v.n;
    }
    return tot;
  }

  /** Mid-LOD meshes are the High-tier crowd casters (SPEC-01 §6). */
  casters(): Mesh[] {
    return [this.variants['midM'], this.variants['midF']].filter((v): v is Variant => !!v).map((v) => v.mesh);
  }

  get radius(): number {
    return this.opts.radius;
  }

  /** Debug counts per variant (visible people by slot). */
  counts(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(this.variants)) out[k] = v.n;
    return out;
  }
}
